import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Plus, UserPlus, Search, ArrowLeft, ChevronRight, X, Loader2, CheckCircle2, Sparkles, Gift } from "lucide-react";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/services/loggingService";
import {
  Dialog,
  DialogContent,
  MobileDialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CompanyAvatar } from "@/components/ui/company-avatar";
import { useAuth } from "@/hooks/auth";
import { InlineLoader } from '@/components/ui/loading';
import { useCompanyIdentifier } from '@/hooks/useCompanyIdentifier';
import { useSimplifiedAuth } from '@/contexts/SimplifiedAuthContext';
import { useTranslation } from '@/hooks/useTranslation';

interface CompanyActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type ActionType = 'create' | 'join' | null;
type Step = 'selection' | 'action' | 'success';

interface CompanyForm {
  name: string;
  description: string;
  industry: string;
  location: string;
}

interface CompanySearchResult {
  id: string;
  name: string;
  company_identifier: string;
  logo_url?: string;
  industry?: string;
  location?: string;
  is_member?: boolean;
  member_role?: string;
}

const INDUSTRY_KEYS = [
  "technology",
  "healthcare",
  "finance",
  "education",
  "manufacturing", 
  "retail",
  "consulting",
  "media",
  "non_profit",
  "other"
] as const;

export const CompanyActionDialog: React.FC<CompanyActionDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const toast = useTranslatedToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refetchData } = useSimplifiedAuth();
  const { t } = useTranslation('pages');
  const { t: tUI } = useTranslation('ui');
  const { t: tCommon } = useTranslation('common');
  
  const [currentStep, setCurrentStep] = useState<Step>('selection');
  const [selectedAction, setSelectedAction] = useState<ActionType>(null);
  const [loading, setLoading] = useState(false);
  const [createdCompany, setCreatedCompany] = useState<{name: string; identifier: string; id?: string; welcomeGift?: any} | null>(null);
  
  // Create Company Form
  const [companyForm, setCompanyForm] = useState<CompanyForm>({
    name: "",
    description: "",
    industry: "",
    location: "",
  });
  
  // Company identifier hook
  const { 
    identifier: companyIdentifier, 
    setIdentifier: setCompanyIdentifier, 
    isChecking: isCheckingIdentifier, 
    isAvailable: isIdentifierAvailable, 
    validationMessage: identifierValidationMessage 
  } = useCompanyIdentifier(companyForm.name);

  const [nameCheckLoading, setNameCheckLoading] = useState(false);
  const [nameExists, setNameExists] = useState(false);
  const [userCompanies, setUserCompanies] = useState<CompanySearchResult[]>([]);

  // Join Company Search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<CompanySearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Search companies with debounce
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data, error } = await supabase
          .from('company_profiles')
          .select(`
            id, 
            name, 
            company_identifier, 
            logo_url, 
            industry, 
            location,
            company_team_members!left (
              user_id,
              member_role
            )
          `)
          .or(`name.ilike.%${searchQuery}%,company_identifier.ilike.%${searchQuery}%`)
          .limit(5);

        if (error) throw error;
        
        // Process results to check membership
        const processedResults = ((data as any[]) || []).map((company: any) => {
          const membership = company.company_team_members?.find(
            (member: any) => member.user_id === user?.id
          );
          
          return {
            id: company.id,
            name: company.name,
            company_identifier: company.company_identifier,
            logo_url: company.logo_url,
            industry: company.industry,
            location: company.location,
            is_member: !!membership,
            member_role: membership?.member_role
          };
        });
        
        setSearchResults(processedResults);
      } catch (error) {
        console.error('Error searching companies:', error);
        toast.error('error.generic');
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Load user's existing companies
  useEffect(() => {
    if (user?.id && isOpen) {
      loadUserCompanies();
    }
  }, [user?.id, isOpen]);

  const loadUserCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('company_team_members')
        .select(`
          company_id,
          member_role,
          company_profiles!inner (
            id,
            name,
            company_identifier,
            logo_url,
            industry,
            location
          )
        `)
        .eq('user_id', user?.id as any);

      if (error) throw error;

      const companies = ((data as any[]) || []).map((item: any) => ({
        id: item.company_profiles.id,
        name: item.company_profiles.name,
        company_identifier: item.company_profiles.company_identifier,
        logo_url: item.company_profiles.logo_url,
        industry: item.company_profiles.industry,
        location: item.company_profiles.location,
        is_member: true,
        member_role: item.member_role
      }));

      setUserCompanies(companies);
    } catch (error) {
      console.error('Error loading user companies:', error);
    }
  };

  // Check company name availability with debounce
  useEffect(() => {
    if (!companyForm.name.trim() || companyForm.name.length < 2) {
      setNameExists(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setNameCheckLoading(true);
      try {
        const { data, error } = await supabase
          .from('company_profiles')
          .select('id')
          .ilike('name', companyForm.name.trim())
          .limit(1);

        if (error) throw error;
        setNameExists(data && data.length > 0);
      } catch (error) {
        console.error('Error checking company name:', error);
      } finally {
        setNameCheckLoading(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [companyForm.name]);

  const handleCreateCompany = async () => {
    if (!companyForm.name.trim()) {
      toast.error('error.validation');
      return;
    }

    // Check if identifier is available before creating
    if (isIdentifierAvailable === false) {
      toast.error('error.validation');
      return;
    }

    if (!user?.id) {
      toast.error('error.permission');
      return;
    }

    setLoading(true);
    try {
      // Final uniqueness check before submission
      if (companyIdentifier) {
        const { data: existingCompany } = await supabase
          .from('company_profiles')
          .select('id')
          .eq('company_identifier', companyIdentifier as any)
          .maybeSingle();
        
        if (existingCompany) {
          toast.error(t('company_management.identifier_already_taken'));
          setLoading(false);
          return;
        }
      }

      const { data: response, error: createError } = await supabase.functions.invoke('company-profile-management', {
        body: {
          action: 'create',
          profileData: {
            name: companyForm.name,
            description: companyForm.description || null,
            industry: companyForm.industry || null,
            location: companyForm.location || null,
            company_identifier: companyIdentifier
          }
        }
      });

      if (createError) throw createError;

      if (response?.success) {
        const newCompanyId = response?.data?.id;
        let giftResult = response.welcomeGift;
        
        // If gift wasn't activated by edge function, try RPC fallback
        if (!giftResult?.success && newCompanyId) {
          logger.info('Welcome gift not activated by edge function, attempting RPC fallback', {
            component: 'CompanyActionDialog',
            metadata: { companyId: newCompanyId, userId: user.id }
          });
          
          try {
            const { data: giftRes, error: giftErr } = await supabase.rpc('consume_welcome_gift_and_upgrade', {
              p_company_id: newCompanyId,
              p_user_id: user.id
            });
            
            if (giftErr) {
              logger.error('Gift RPC failed', giftErr, {
                component: 'CompanyActionDialog',
                metadata: { companyId: newCompanyId, userId: user.id }
              });
            } else if (giftRes) {
              giftResult = giftRes;
              logger.info('Welcome gift activated via RPC fallback', {
                component: 'CompanyActionDialog',
                metadata: { companyId: newCompanyId, giftResult }
              });
            }
          } catch (rpcError) {
            logger.error('Exception during gift RPC call', rpcError, {
              component: 'CompanyActionDialog',
              metadata: { companyId: newCompanyId, userId: user.id }
            });
          }
        }
        
        // Show gift success if activated
        if (giftResult?.success) {
          toast.success(
            `🎉 ${giftResult.message || t('company_management.welcome_gift_activated')}`,
            { duration: 6000 }
          );
        }
        
        // Store created company info for success screen
        setCreatedCompany({
          name: companyForm.name,
          identifier: companyIdentifier,
          id: newCompanyId,
          welcomeGift: giftResult || null
        });
        
        // Show success toast
        toast.success(t('company_management.success_title'));
        
        // Refetch auth data to sync new company
        setTimeout(() => refetchData(), 100);
        
        // Close dialog and navigate to dashboard
        setTimeout(() => {
          handleGoToDashboard();
        }, 1500);
      }
    } catch (error: any) {
      console.error('Edge function failed, attempting direct insert fallback:', error);

      // Fallback: try direct insert via RLS using the user's session
      try {
        const { data: inserted, error: insertError } = await supabase
          .from('company_profiles')
          .insert({
            name: companyForm.name,
            description: companyForm.description || null,
            industry: companyForm.industry || null,
            location: companyForm.location || null,
            company_identifier: companyIdentifier,
            user_id: user.id,
          } as any)
          .select('id, name, company_identifier')
          .maybeSingle();

        if (insertError) {
          throw insertError;
        }

        if (inserted) {
          let giftResult = null;
          
          // Always try to activate welcome gift via RPC in fallback path
          logger.info('Attempting welcome gift activation via RPC (fallback path)', {
            component: 'CompanyActionDialog',
            metadata: { companyId: (inserted as any).id, userId: user.id }
          });
          
          try {
            const { data: giftRes, error: giftErr } = await supabase.rpc('consume_welcome_gift_and_upgrade', {
              p_company_id: (inserted as any).id,
              p_user_id: user.id
            });
            
            if (giftErr) {
              logger.error('Gift RPC failed in fallback', giftErr, {
                component: 'CompanyActionDialog',
                metadata: { companyId: (inserted as any).id, userId: user.id }
              });
            } else if (giftRes) {
              giftResult = giftRes;
              logger.info('Welcome gift activated via RPC in fallback', {
                component: 'CompanyActionDialog',
                metadata: { companyId: (inserted as any).id, giftResult }
              });
              
              // Show gift success toast
              toast.success(
                `🎉 ${giftResult.message || t('company_management.welcome_gift_activated')}`,
                { duration: 6000 }
              );
            }
          } catch (rpcError) {
            logger.error('Exception during gift RPC call in fallback', rpcError, {
              component: 'CompanyActionDialog',
              metadata: { companyId: (inserted as any).id, userId: user.id }
            });
          }
          
          // Store created company info for success screen
          setCreatedCompany({
            name: companyForm.name,
            identifier: companyIdentifier,
            id: (inserted as any).id,
            welcomeGift: giftResult
          });
          
          // Show success toast
          toast.success(t('company_management.success_title'));
          
          // Refetch auth data to sync new company
          setTimeout(() => refetchData(), 100);
          
          // Close dialog and navigate to dashboard
          setTimeout(() => {
            handleGoToDashboard();
          }, 1500);

          return;
        }

        // If still no data, throw a generic error
        throw new Error('Unknown error creating company');
      } catch (fallbackErr: any) {
        console.error('Direct insert fallback also failed:', fallbackErr);
        toast.raw.error(fallbackErr?.message || 'Failed to create company');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCompany = async (company: CompanySearchResult) => {
    setLoading(true);
    try {
      // For now, just show success message
      // In a real implementation, this would send a join request
      toast.raw.success(t('company_management.join_request_sent', { companyName: company.name }));
      
      setSearchQuery('');
      setSearchResults([]);
    } catch (error: any) {
      console.error('Error joining company:', error);
      toast.raw.error(error.message || t('company_management.join_request_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset all form state when closing
    setCompanyForm({
      name: "",
      description: "",
      industry: "",
      location: "",
    });
    setSearchQuery("");
    setSearchResults([]);
    setCurrentStep('selection');
    setSelectedAction(null);
    setCreatedCompany(null);
    onClose();
  };

  const handleGoToDashboard = () => {
    // Reset form state
    setCompanyForm({
      name: "",
      description: "",
      industry: "",
      location: "",
    });
    setCurrentStep('selection');
    setSelectedAction(null);
    setCreatedCompany(null);
    onClose();
    
    // Navigate to company dashboard
    navigate('/workspace/company/hiring-center');
  };

  const handleCreateAnother = () => {
    // Reset to create form
    setCompanyForm({
      name: "",
      description: "",
      industry: "",
      location: "",
    });
    setCurrentStep('action');
    setSelectedAction('create');
    setCreatedCompany(null);
  };

  const handleActionSelect = (action: ActionType) => {
    setSelectedAction(action);
    setCurrentStep('action');
  };

  const handleBackToSelection = () => {
    setCurrentStep('selection');
    setSelectedAction(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
        <MobileDialogContent 
          className="p-0 flex flex-col overflow-hidden md:max-w-2xl md:rounded-xl"
        >
        <DialogHeader className="px-3 py-2 md:px-4 md:py-3 shrink-0 space-y-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {currentStep === 'action' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToSelection}
                  className="mr-2 p-1"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <Building2 className="h-5 w-5" />
            <DialogTitle>
              {currentStep === 'selection' ? t('company_management.selection_title') : 
               currentStep === 'success' ? t('company_management.success_title') :
               selectedAction === 'create' ? t('company_management.create_title') : t('company_management.join_title')}
            </DialogTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {currentStep !== 'selection' && (
            <DialogDescription>
              {currentStep === 'success'
              ? t('company_management.success_description')
              : selectedAction === 'create'
              ? t('company_management.create_description')
              : t('company_management.join_description')
              }
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto px-3 pb-2 md:px-4">
        {currentStep === 'success' && createdCompany ? (
          // Success Screen
            <div className="relative flex flex-col items-center justify-start py-4 px-3">
              <div className="space-y-3 text-center relative z-10">
                <div className="space-y-2">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto animate-scale-in">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                
                <h3 className="text-xl font-semibold text-foreground">
                  🎉 {t('company_management.success_title')}
                </h3>
                
                  <div className="space-y-1">
                    <p className="text-base text-muted-foreground">
                    {t('company_management.success_message', { companyName: createdCompany.name })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('company_management.company_identifier_info', { identifier: createdCompany.identifier })}
                  </p>
                </div>
                
                  <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <p className="text-sm text-primary">
                    🚀 {t('company_management.success_subtitle')}
                  </p>
                </div>
                
                {/* Welcome Gift Badge */}
                {createdCompany?.welcomeGift?.success && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Gift className="h-5 w-5 text-primary mt-0.5 animate-bounce" />
                      <div className="text-left">
                        <p className="font-semibold text-primary">🎉 {t('company_management.welcome_gift_title')}</p>
                        <p 
                          className="text-sm text-muted-foreground mt-1"
                          dangerouslySetInnerHTML={{ __html: t('company_management.welcome_gift_description') }}
                        />
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1 ml-4">
                          <li dangerouslySetInnerHTML={{ __html: '✓ ' + t('company_management.welcome_gift_credits') }} />
                          <li dangerouslySetInnerHTML={{ __html: '✓ ' + t('company_management.welcome_gift_job_limit') }} />
                          <li dangerouslySetInnerHTML={{ __html: '✓ ' + t('company_management.welcome_gift_expires') }} />
                        </ul>
                        <p className="text-xs text-muted-foreground mt-2">
                          {t('company_management.welcome_gift_upgrade_hint')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-2 pt-3">
                  <Button
                    onClick={handleGoToDashboard}
                    className="gap-2"
                  >
                    {tUI('company_management.go_to_dashboard')}
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={handleCreateAnother}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {tUI('company_management.create_another')}
                  </Button>
                </div>
              </div>
            </div>
        ) : currentStep === 'selection' ? (
          // Step 1: Selection Screen
          <div className="space-y-2 pb-1.5">
              <div className="grid gap-3">
              {/* Create Company Option */}
              <div 
              className="p-3 rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 hover:bg-primary/5 transition-all cursor-pointer group"
                onClick={() => handleActionSelect('create')}
              >
              <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Plus className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-base">{t('company_management.create_option_title')}</h3>
                      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('company_management.create_option_description')}
                    </p>
                    <p className="text-xs text-muted-foreground pt-1">
                      {tUI('company_management.company_features.post_jobs')} • {tUI('company_management.company_features.invite_members')} • {tUI('company_management.company_features.access_analytics')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Join Company Option */}
              <div 
                className="p-3 rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 hover:bg-primary/5 transition-all cursor-pointer group"
                onClick={() => handleActionSelect('join')}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                    <UserPlus className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-base">{t('company_management.join_option_title')}</h3>
                      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('company_management.join_option_description')}
                    </p>
                    <p className="text-xs text-muted-foreground pt-1">
                      {tUI('company_management.company_features.search_companies')} • {tUI('company_management.company_features.send_requests')} • {tUI('company_management.company_features.access_resources')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : selectedAction === 'create' ? (
          // Step 2: Create Company Form
          <div className="space-y-2 py-1.5">
            {/* Show user's existing companies */}
              {userCompanies.length > 0 && (
                <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t('company_management.existing_companies')}</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {userCompanies.map((company) => (
                    <div
                      key={company.id}
                      className="flex items-center justify-between p-2 rounded-lg border bg-muted/10"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <CompanyAvatar
                          src={company.logo_url}
                          name={company.name}
                          size="sm"
                          className="h-8 w-8 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{company.name}</p>
                          <p className="text-xs text-muted-foreground">{t('company_management.role_label')}: {tCommon(`roles.${company.member_role}`)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid gap-2">
                <div className="grid gap-1.5">
                <Label htmlFor="company-name">{t('company_management.company_name_label')} *</Label>
                <div className="relative">
                  <Input
                    id="company-name"
                    value={companyForm.name}
                    onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})}
                    placeholder={t('company_management.company_name_placeholder')}
                    disabled={loading}
                    className={nameExists ? "border-destructive" : ""}
                  />
                  {nameCheckLoading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <InlineLoader size="sm" />
                    </div>
                  )}
                </div>
                {nameExists && (
                  <p className="text-sm text-destructive">
                    {t('company_management.company_name_exists')}
                  </p>
                )}
              </div>
              
                <div className="grid gap-1.5">
                <Label htmlFor="company-identifier">{tUI('company_management.company_identifier')} *</Label>
                <div className="relative">
                  <Input
                    id="company-identifier"
                    value={companyIdentifier}
                    onChange={(e) => setCompanyIdentifier(e.target.value)}
                    placeholder={t('company_management.company_identifier_placeholder')}
                    disabled={loading}
                    className={`pr-8 ${
                      isIdentifierAvailable === false 
                        ? 'border-destructive focus-visible:ring-destructive' 
                        : isIdentifierAvailable === true 
                        ? 'border-green-500 focus-visible:ring-green-500' 
                        : ''
                    }`}
                  />
                  {isCheckingIdentifier && (
                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
                  <p className="text-xs text-muted-foreground">{t('company_management.used_in_company_url')}</p>
                {identifierValidationMessage && (
                  <p className={`text-xs mt-1 ${
                    isIdentifierAvailable === true 
                      ? 'text-green-600' 
                      : isIdentifierAvailable === false 
                      ? 'text-destructive' 
                      : 'text-muted-foreground'
                  }`}>
                    {identifierValidationMessage}
                  </p>
                )}
              </div>

                <div className="grid gap-1.5">
                <Label htmlFor="company-description">{tUI('company_management.description')}</Label>
                <Textarea
                  id="company-description"
                  value={companyForm.description}
                  onChange={(e) => setCompanyForm({...companyForm, description: e.target.value})}
                  placeholder={tUI('company_management.description_placeholder')}
                  disabled={loading}
                />
              </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                  <Label htmlFor="company-industry">{tUI('company_management.industry')}</Label>
                  <Select
                    value={companyForm.industry}
                    onValueChange={(value) => setCompanyForm({...companyForm, industry: value})}
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={tUI('company_management.select_industry')} />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRY_KEYS.map((industryKey) => (
                        <SelectItem key={industryKey} value={industryKey}>
                          {tCommon(`industries.${industryKey}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                  <div className="grid gap-1.5">
                  <Label htmlFor="company-location">{tUI('company_management.location')}</Label>
                  <Input
                    id="company-location"
                    value={companyForm.location}
                    onChange={(e) => setCompanyForm({...companyForm, location: e.target.value})}
                    placeholder={tUI('company_management.location_placeholder')}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={handleCreateCompany} disabled={loading || !companyForm.name.trim() || !companyIdentifier.trim() || isIdentifierAvailable === false || isCheckingIdentifier || nameExists || nameCheckLoading}>
                {loading ? tUI('company_management.creating') : tUI('company_management.create_company')}
              </Button>
            </div>
          </div>
        ) : (
          // Step 2: Join Company Form
            <div className="space-y-2 py-1.5">
            <div className="grid gap-2">
                <div className="grid gap-1.5">
                <Label htmlFor="company-search">{tUI('company_management.search_companies')}</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="company-search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={tUI('company_management.search_placeholder')}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Search Results */}
              {searchLoading && (
                <div className="flex justify-center py-3">
                  <div className="text-sm text-muted-foreground">{tUI('company_management.searching')}</div>
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  <Label className="text-sm font-medium">{t('company_management.search_results')}</Label>
                  {searchResults.map((company) => (
                    <div
                      key={company.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-muted/20 hover:bg-muted/40 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <CompanyAvatar
                          src={company.logo_url}
                          name={company.name}
                          size="sm"
                          className="h-10 w-10 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{company.name}</p>
                          <p className="text-sm text-muted-foreground">@{company.company_identifier}</p>
                          {(company.industry || company.location) && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {[company.industry, company.location].filter(Boolean).join(" • ")}
                            </p>
                          )}
                        </div>
                      </div>
                      {company.is_member ? (
                        <div className="flex flex-col items-end">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled
                            className="cursor-not-allowed opacity-60"
                          >
                            {tUI('company_management.already_joined')}
                          </Button>
                          <span className="text-xs text-muted-foreground mt-1">
                            {tUI('company_management.role_label')} {tCommon(`roles.${company.member_role}`)}
                          </span>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleJoinCompany(company)}
                          disabled={loading}
                        >
                          {loading ? tUI('company_management.sending') : tUI('company_management.request_to_join')}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {searchQuery.length >= 2 && !searchLoading && searchResults.length === 0 && (
                    <div className="text-center py-4 text-muted-foreground">
                    <Building2 className="h-10 w-10 mx-auto mb-2 opacity-20" />
                  <p>{t('company_management.no_companies_found', { searchQuery })}</p>
                  <p className="text-sm">{t('company_management.search_different_keywords')}</p>
                </div>
              )}

              {searchQuery.length < 2 && (
                  <div className="text-center py-4 text-muted-foreground">
                    <Search className="h-10 w-10 mx-auto mb-2 opacity-20" />
                  <p>{t('company_management.search_min_characters')}</p>
                  <p className="text-sm">{t('company_management.search_by_company_hint')}</p>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </MobileDialogContent>
    </Dialog>
  );
};